import {
  Button,
  DatePicker,
  ExpandCircleDownIcon,
  Input,
  Label,
  Modal,
  Select,
  showToast,
  Tag,
  Text,
} from '@mercai/clever'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { QuotationDeliveryEnum, QuotationStatusEnum } from '@/types'
import { useTranslation } from 'react-i18next'
import { RfqStatusSelectProps } from './quotation-status-select.type'

import { onErrorMessage, trackEvent, useValidationErrors } from '@/helpers'
import { QUERIES_ADMIN, useEditAddress, useUpdateQuotation } from '@/services'
import { useQueryClient } from '@tanstack/react-query'
import {
  availableQuotationStatuses,
  checkExpiresAt,
  schemaMapsUrl,
} from './quotation-status-select.constants'
import styles from './quotation-status-select.module.css'

export const QuotationStatusSelect = ({ quotation }: RfqStatusSelectProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const queryClient = useQueryClient()
  const [deliveryOption, setDeliveryOption] = useState<
    'pick_up' | 'receive_at_address'
  >()

  const [mapsUrl, setMapsUrl] = useState<string>('')

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaMapsUrl(t),
    { mapsUrl },
  )

  const { mutateAsync: mutateAsyncEditAddress } = useEditAddress(
    quotation?.deliveryAddress?.uuid || '',
  )

  const [modalType, setModalType] = useState<
    | 'is_cancellation'
    | 'not_has_delivery_option'
    | 'not_has_maps_url'
    | 'needs_new_expiration'
  >('is_cancellation')

  const [statusToModal, setStatusToModal] = useState<string>('')

  const [invalidateReason, setInvalidateReason] = useState('')

  const [showError, setShowError] = useState(false)

  const [isOpen, setIsOpen] = useState(false)
  const [isOpenModal, setIsOpenModal] = useState(false)

  const [newExpiration, setNewExpiration] = useState<string>()

  const buttonRef = useRef<HTMLDivElement | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)

  const stateObject: Record<QuotationStatusEnum, ReactNode> = {
    created: (
      <Tag variant="basic" model="light">
        {tEnums('quotation_status_created')}
        <ExpandCircleDownIcon style={{ fill: 'var(--gray-800)' }} />
      </Tag>
    ),
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('quotation_status_pending')}
        <ExpandCircleDownIcon style={{ fill: 'var(--warning-800)' }} />
      </Tag>
    ),
    active: (
      <Tag variant="primary" model="light">
        {tEnums('quotation_status_active')}
        <ExpandCircleDownIcon style={{ fill: 'var(--primary-800)' }} />
      </Tag>
    ),
    ready: (
      <Tag variant="basic" model="light">
        {tEnums('quotation_status_ready')}
        <ExpandCircleDownIcon style={{ fill: 'var(--gray-800)' }} />
      </Tag>
    ),
    accepted: (
      <Tag variant="success" model="light">
        {tEnums('quotation_status_accepted')}
        <ExpandCircleDownIcon style={{ fill: 'var(--success-800)' }} />
      </Tag>
    ),
    expired: (
      <Tag variant="basic" model="light">
        {tEnums('quotation_status_expired')}
        <ExpandCircleDownIcon style={{ fill: 'var(--basic-800)' }} />
      </Tag>
    ),
    rejected: (
      <Tag variant="danger" model="light">
        {tEnums('quotation_status_rejected')}
        <ExpandCircleDownIcon style={{ fill: 'var(--danger-800)' }} />
      </Tag>
    ),
    pending_approval: (
      <Tag variant="warning" model="light">
        {tEnums('quotation_status_pending_approval')}
        <ExpandCircleDownIcon style={{ fill: 'var(--warning-800)' }} />
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('quotation_status_cancelled')}
        <ExpandCircleDownIcon style={{ fill: 'var(--danger-800)' }} />
      </Tag>
    ),
  }

  const { mutateAsync: mutateAsyncUpdateQuotation, isPending } =
    useUpdateQuotation(quotation?.uuid || '')

  const togglePopper = () => {
    setIsOpen((prev) => !prev)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  const onHandleCloseModal = () => {
    setMapsUrl('')
    setStatusToModal('')
    setInvalidateReason('')
    setShowError(false)
    setIsOpenModal(false)
    setDeliveryOption(undefined)
    setNewExpiration('')
  }

  const onHandleOpenModal = (val: string) => {
    if (val === 'ready' && !quotation?.deliveryAddress?.mapsUrl) {
      setModalType('not_has_maps_url')
    }

    if (val === 'rejected' || val === '') {
      setModalType('is_cancellation')
    }

    if (val === 'accepted') {
      setModalType('not_has_delivery_option')
    }

    if (val === 'active') {
      setNewExpiration(quotation?.expiresAt)
      setModalType('needs_new_expiration')
    }

    setMapsUrl('')
    setInvalidateReason('')
    setStatusToModal(val)
    setIsOpenModal(true)
    setIsOpen(false)
  }

  const onSubmit = async (val: keyof typeof QuotationStatusEnum) => {
    setIsOpen(false)

    if (
      (val === 'rejected' && !invalidateReason) ||
      (val === 'accepted' && !deliveryOption && isOpenModal)
    ) {
      return setShowError(true)
    }

    const payload: Record<string, unknown> = {
      uuid: quotation?.uuid,
      status: val,
      expiresAt: newExpiration || quotation?.expiresAt,
      ...(invalidateReason ? { rejectedReason: invalidateReason } : {}),
      ...(deliveryOption ? { deliveryOption } : {}),
      ...(val === 'accepted' || val === 'rejected'
        ? { acceptedOrRejectedFrom: 'opus' }
        : {}),
    }

    const response = await mutateAsyncUpdateQuotation(payload)

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION.module],
      })

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_ADDRESS.module],
      })

      trackEvent('adminQuotationUpdateStatusClick', {
        status: val,
      })

      onHandleCloseModal()
    } else {
      onErrorMessage(
        t('quotationStatusSelectErrorTitle'),
        t('quotationStatusSelectErrorMessage'),
      )
    }
  }

  const onSubmitMapsUrl = async (val: keyof typeof QuotationStatusEnum) => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }
    onHandleListenErrors(false)

    const response = await mutateAsyncEditAddress({
      ...quotation?.deliveryAddress,
      uuid: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      mapsUrl,
    })

    if (!response.success) {
      return showToast({
        variant: 'error',
        title: response?.message,
      })
    }

    onSubmit(val)
  }

  const allowedStatuses = Object.keys(QuotationStatusEnum).filter((key) =>
    availableQuotationStatuses[quotation?.status || 'pending'].includes(
      key as keyof typeof QuotationStatusEnum,
    ),
  )

  const handleOnClick = (key: string) => {
    if (key === 'active' && checkExpiresAt(quotation?.expiresAt)) {
      return onHandleOpenModal(key)
    }

    if (
      key === '' ||
      key === 'rejected' ||
      (!quotation?.deliveryOption && key === 'accepted') ||
      (key === 'ready' && !quotation?.deliveryAddress?.mapsUrl)
    ) {
      return onHandleOpenModal(key)
    }

    onSubmit(key as keyof typeof QuotationStatusEnum)
  }

  const modalContents = {
    is_cancellation: (
      <>
        <Modal.Header title={t('quotationStatusSelectInvalidateModalTitle')} />
        <Modal.Body className={styles['modal-body']}>
          <div>
            <Label>
              {t('quotationStatusSelectInvalidateModalStatusLabel')}
            </Label>
            <Input
              className={styles['status-input']}
              value={
                statusToModal ? tEnums(`quotation_status_${statusToModal}`) : ''
              }
            />
          </div>
          <div>
            <Label className={styles['reason-label']}>
              {t('quotationStatusSelectInvalidateModalReasonLabel')}
              <span>*</span>
            </Label>
            <Input
              placeholder={t(
                'quotationStatusSelectInvalidateModalReasonPlaceholder',
              )}
              value={invalidateReason}
              onChange={(value) => setInvalidateReason(value)}
              error={showError ? t('requiredMessage') : ''}
              isTextArea
            />
          </div>
          <div className={styles['modal-footer']}>
            <Button variant="secondary" onClick={onHandleCloseModal}>
              {t('quotationStatusCancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={() => onSubmit(statusToModal as 'rejected')}
              disabled={isPending}
            >
              {t('quotationStatusConfirmButton')}
            </Button>
          </div>
        </Modal.Body>
      </>
    ),
    not_has_delivery_option: (
      <>
        <Modal.Header
          title={t('quotationStatusSelectDeliveryOptionModalTitle')}
        />
        <Modal.Body className={styles['modal-body']}>
          <div>
            <Label>
              {t('quotationStatusSelectInvalidateModalStatusLabel')}
            </Label>
            <Input
              className={styles['status-input']}
              value={
                statusToModal ? tEnums(`quotation_status_${statusToModal}`) : ''
              }
            />
          </div>
          <div>
            <Label className={styles['reason-label']}>
              {t('quotationStatusSelectDeliveryOptionModalLabel')}
              <span>*</span>
            </Label>
            <Select
              value={deliveryOption}
              placeholder={t(
                'quotationStatusSelectDeliveryOptionModalPlaceholder',
              )}
              options={Object.keys(QuotationDeliveryEnum).map((item) => ({
                label: tEnums(`logistic_shipment_delivery_option_${item}`),
                value: item,
              }))}
              onChange={(value) => setDeliveryOption(value as 'pick_up')}
              error={showError ? t('requiredMessage') : ''}
            />
          </div>
          <div className={styles['modal-footer']}>
            <Button variant="secondary" onClick={onHandleCloseModal}>
              {t('quotationStatusCancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={() => onSubmit(statusToModal as 'rejected')}
              disabled={isPending}
            >
              {t('quotationStatusConfirmButton')}
            </Button>
          </div>
        </Modal.Body>
      </>
    ),
    not_has_maps_url: (
      <>
        <Modal.Header title={t('quotationStatusMapsUrlModalTitle')} />
        <Modal.Body className={styles['modal-body']}>
          <Text>{t('quotationStatusMapsUrlModalDescription')}</Text>
          <div>
            <Label required>
              {t('quotationStatusMapsUrlModalMapsUrlLabel')}
            </Label>
            <Input
              value={mapsUrl}
              onChange={(val) => setMapsUrl(val)}
              error={errors?.mapsUrl || undefined}
            />
          </div>

          <div className={styles['modal-footer']}>
            <Button variant="secondary" onClick={onHandleCloseModal}>
              {t('quotationStatusCancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                onSubmitMapsUrl(
                  statusToModal as keyof typeof QuotationStatusEnum,
                )
              }
              disabled={isPending}
            >
              {t('quotationStatusConfirmButton')}
            </Button>
          </div>
        </Modal.Body>
      </>
    ),
    needs_new_expiration: (
      <>
        <Modal.Header title={t('quotationStatusNewExpirationModalTitle')} />
        <Modal.Body className={styles['modal-body']}>
          <Text>{t('quotationStatusNewExpirationModalDescription')}</Text>
          <div>
            <Label className={styles['reason-label']}>
              {t('quotationStatusNewExpirationModalLabel')}
            </Label>
            <DatePicker
              value={newExpiration}
              onChange={(val) => setNewExpiration(val)}
            />
          </div>
          <div className={styles['modal-footer']}>
            <Button variant="secondary" onClick={onHandleCloseModal}>
              {t('quotationStatusCancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={() => onSubmit(statusToModal as 'active')}
              disabled={isPending}
            >
              {t('quotationStatusConfirmButton')}
            </Button>
          </div>
        </Modal.Body>
      </>
    ),
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className={styles.core}>
      <div
        ref={buttonRef}
        onClick={togglePopper}
        className={styles['status-button']}
      >
        {stateObject[quotation?.status || 'pending']}
      </div>
      {isOpen && (
        <div ref={popperRef} className={styles.popper}>
          <ul>
            {allowedStatuses.map((key) => (
              <li
                key={key}
                className={key === quotation?.status ? styles.active : ''}
                onClick={() => handleOnClick(key)}
              >
                {tEnums(`quotation_status_${key}`)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Modal.Root
        isOpen={isOpenModal}
        onHandleOpen={onHandleCloseModal}
        size="medium"
      >
        {modalContents[modalType]}
      </Modal.Root>
    </div>
  )
}
